import React, { useState, useRef, useEffect } from 'react';

// Camera-only video recorder for submissions.
// Flow: turn on camera -> 3-2-1 -> record (auto-stops at MAX_SECONDS) -> review -> record again.
// `value` is a base64 data URL (new recording) or a saved video URL; `onChange` receives the data URL.

const MAX_SECONDS = 120;
const MAX_BYTES = 30 * 1024 * 1024;

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

// MP4 where the browser can, so files play everywhere; otherwise WebM
const pickMimeType = () => {
  const options = ['video/mp4;codecs=avc1,mp4a', 'video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return options.find((t) => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || '';
};

const cameraErrorText = (err) => {
  switch (err && err.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Camera access was blocked. Click the camera icon in the address bar, choose Allow, then try again.';
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No camera or microphone was found. Plug one in, or try another device.';
    case 'NotReadableError':
      return 'Your camera is in use by another app (Zoom, Teams, FaceTime). Close it and try again.';
    default:
      return "The camera couldn't start. Reload the page and try again.";
  }
};

const VideoRecorder = ({ value, onChange, instructions, disabled = false }) => {
  // idle | camera | countdown | recording | processing
  const [stage, setStage] = useState('idle');
  const [countdown, setCountdown] = useState(3);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');

  const liveRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  // Latest onChange, so the recorder's async callback never uses a stale one
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  // Release the camera and timer when the component goes away
  useEffect(() => () => {
    clearInterval(timerRef.current);
    stopCamera();
  }, []);

  // Attach the live stream once the <video> element is on screen
  useEffect(() => {
    if (liveRef.current && streamRef.current && liveRef.current.srcObject !== streamRef.current) {
      liveRef.current.srcObject = streamRef.current;
    }
  }, [stage]);

  const startCamera = async () => {
    setError('');
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setError('This page must be opened over https:// to use the camera.');
      return;
    }
    if (!pickMimeType()) {
      setError("This browser can't record video. Please use a recent Chrome, Edge, Firefox or Safari.");
      return;
    }
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } },
        audio: { echoCancellation: true, noiseSuppression: true }
      });
      setStage('camera');
    } catch (err) {
      setError(cameraErrorText(err));
    }
  };

  const startCountdown = async () => {
    setStage('countdown');
    for (let n = 3; n > 0; n--) {
      setCountdown(n);
      await new Promise((r) => setTimeout(r, 1000));
    }
    startRecording();
  };

  const startRecording = () => {
    const mimeType = pickMimeType();
    chunksRef.current = [];
    let recorder;
    try {
      recorder = new MediaRecorder(streamRef.current, { mimeType, videoBitsPerSecond: 1000000, audioBitsPerSecond: 64000 });
    } catch {
      recorder = new MediaRecorder(streamRef.current);
    }
    recorder.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    recorder.onstop = onRecordingStopped;
    recorder.start(1000);
    recorderRef.current = recorder;

    const startedAt = Date.now();
    setSeconds(0);
    setStage('recording');
    timerRef.current = setInterval(() => {
      const s = (Date.now() - startedAt) / 1000;
      setSeconds(Math.min(s, MAX_SECONDS));
      if (s >= MAX_SECONDS) stopRecording();
    }, 250);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const onRecordingStopped = () => {
    const type = (recorderRef.current?.mimeType || 'video/webm').split(';')[0];
    const blob = new Blob(chunksRef.current, { type });
    recorderRef.current = null;
    chunksRef.current = [];
    stopCamera();

    if (blob.size > MAX_BYTES) {
      setError(`This video is ${(blob.size / 1048576).toFixed(0)} MB, over the ${MAX_BYTES / 1048576} MB limit. Please record again.`);
      setStage('idle');
      return;
    }

    setStage('processing');
    const reader = new FileReader();
    reader.onloadend = () => {
      onChangeRef.current(reader.result);
      setStage('idle');
    };
    reader.readAsDataURL(blob);
  };

  const recordAgain = () => {
    onChange('');
    startCamera();
  };

  const btn = 'px-4 py-2 rounded-md text-sm font-medium text-white transition-colors disabled:opacity-50';

  return (
    <div className="space-y-3">
      {instructions && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-[#054581] whitespace-pre-line">
          <span className="font-semibold">What to include in your video: </span>
          {instructions}
        </div>
      )}

      {/* Live camera / countdown / recording */}
      {['camera', 'countdown', 'recording'].includes(stage) && (
        <div className="relative bg-black rounded-md overflow-hidden">
          <video ref={liveRef} autoPlay muted playsInline className="w-full max-h-80 object-contain" style={{ transform: 'scaleX(-1)' }} />
          {stage === 'countdown' && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-6xl font-bold bg-black/40">
              {countdown}
            </div>
          )}
          {stage === 'recording' && (
            <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-semibold text-white ${MAX_SECONDS - seconds <= 15 ? 'bg-red-600' : 'bg-black/60'}`}>
              ● {fmt(seconds)} / {fmt(MAX_SECONDS)}
            </div>
          )}
        </div>
      )}

      {/* Recorded / saved video */}
      {stage === 'idle' && value && (
        <video src={value} controls playsInline className="w-full max-h-80 bg-black rounded-md" />
      )}

      {stage === 'processing' && <p className="text-sm text-gray-600">Preparing your video…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-2">
        {stage === 'idle' && !value && (
          <button type="button" onClick={startCamera} disabled={disabled} className={`${btn} bg-[#1086a4] hover:bg-[#0d6d86]`}>
            Turn on camera
          </button>
        )}
        {stage === 'idle' && value && (
          <button type="button" onClick={recordAgain} disabled={disabled} className={`${btn} bg-gray-600 hover:bg-gray-700`}>
            Record again
          </button>
        )}
        {stage === 'camera' && (
          <>
            <button type="button" onClick={startCountdown} className={`${btn} bg-red-600 hover:bg-red-700`}>
              Start recording
            </button>
            <button type="button" onClick={() => { stopCamera(); setStage('idle'); }} className={`${btn} bg-gray-500 hover:bg-gray-600`}>
              Cancel
            </button>
          </>
        )}
        {stage === 'recording' && (
          <button type="button" onClick={stopRecording} className={`${btn} bg-red-600 hover:bg-red-700`}>
            Stop recording
          </button>
        )}
      </div>
      {stage === 'camera' && (
        <p className="text-xs text-gray-500">You'll get a 3-second countdown. Recording stops automatically at {fmt(MAX_SECONDS)}.</p>
      )}
    </div>
  );
};

export default VideoRecorder;
